import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Menu,
  ListItemText,
  TableSortLabel
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { usePermissions } from '../hooks/usePermissions';
import ColumnFilter, { FilterConfig } from './ColumnFilter';

interface Vendor {
  id: number;
  name: string;
  status: string;
}

const Vendors: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || '';
  const { hasPermission } = usePermissions(role);
  
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState(false);
  const [showAddVendorDialog, setShowAddVendorDialog] = useState(false);
  const [showEditVendorDialog, setShowEditVendorDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [newVendor, setNewVendor] = useState({
    name: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('name');
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});

  const columnFilterConfigs: FilterConfig[] = [
    { field: 'name', type: 'string', label: 'Vendor Name', placeholder: 'Filter by vendor name' },
    { field: 'status', type: 'select', label: 'Status', options: [
      { value: 'Active', label: 'Active' },
      { value: 'Blocked', label: 'Blocked' }
    ]}
  ];

  useEffect(() => {
    if (token) {
      fetchVendors();
    }
  }, [token]);

  const fetchVendors = async () => {
    setIsLoadingVendors(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/vendors`, {
        headers: { Authorization: token }
      });
      setVendors(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching vendors:', err);
      }
    } finally {
      setIsLoadingVendors(false);
    }
  };

  const handleAddVendor = async () => {
    if (!newVendor.name.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`${API_BASE_URL}/vendors`, { name: newVendor.name, status: 'Active' }, {
        headers: { Authorization: token }
      });
      
      setShowAddVendorDialog(false);
      setNewVendor({ name: '' });
      fetchVendors();
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        console.error('You do not have permission to add vendors');
      } else {
        console.error('Failed to add vendor');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditVendor = async () => {
    if (!editingVendor) return;
    
    setIsSubmitting(true);
    try {
      // Only send status - name cannot be changed
      const vendorData: any = {
        status: editingVendor.status
      };
      
      const response = await axios.put(
        `${API_BASE_URL}/vendors/${editingVendor.id}`,
        vendorData,
        {
          headers: { Authorization: token }
        }
      );
      
      setVendors(prev => prev.map(vendor => 
        vendor.id === editingVendor.id ? response.data : vendor
      ));
      setShowEditVendorDialog(false);
      setEditingVendor(null);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        console.error('You do not have permission to edit vendors');
      } else {
        console.error('Failed to update vendor');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (action: string) => {
    handleActionsMenuClose();
    switch (action) {
      case 'add':
        setShowAddVendorDialog(true);
        break;
    }
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleColumnFilterChange = (field: string, value: any) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnFilterClear = (field: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const sortedVendors = [...vendors].filter(vendor => {
    return Object.entries(columnFilters).every(([field, filterValue]) => {
      if (!filterValue || filterValue === '') return true;
      
      switch (field) {
        case 'name':
          return vendor.name.toLowerCase().includes(filterValue.toLowerCase());
        case 'status':
          return vendor.status === filterValue;
        default:
          return true;
      }
    });
  }).sort((a, b) => {
    let aValue: any;
    let bValue: any;
    
    switch (orderBy) {
      case 'name':
        aValue = a.name;
        bValue = b.name;
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        aValue = a[orderBy as keyof Vendor];
        bValue = b[orderBy as keyof Vendor];
    }
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  return (
    <Box sx={{ width: '100%' }}>
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {hasPermission('add-vendor') && (
            <>
              <Button
                variant="outlined"
                onClick={handleActionsMenuOpen}
                fullWidth
                sx={{ 
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                Actions
              </Button>
              <Menu
                anchorEl={actionsMenuAnchor}
                open={Boolean(actionsMenuAnchor)}
                onClose={handleActionsMenuClose}
                PaperProps={{
                  style: {
                    maxHeight: '70vh',
                    width: '100%',
                    maxWidth: '280px'
                  }
                }}
              >
                <MenuItem onClick={() => handleActionSelect('add')}>
                  <ListItemText primary="Add Vendor" />
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, maxWidth: '600px' }}>
              {/* Search can be added here if needed */}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
              {hasPermission('add-vendor') && (
                <Button variant="contained" onClick={() => setShowAddVendorDialog(true)} size="small">
                  Add Vendor
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 'calc(100vh - 300px)',
          '& .MuiTableCell-root': {
            padding: '8px 16px',
            fontSize: '0.875rem'
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Vendor Name
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[0]}
                    value={columnFilters.name}
                    onChange={(value) => handleColumnFilterChange('name', value)}
                    onClear={() => handleColumnFilterClear('name')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[1]}
                    value={columnFilters.status}
                    onChange={(value) => handleColumnFilterChange('status', value)}
                    onClear={() => handleColumnFilterClear('status')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingVendors ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              sortedVendors.map((vendor) => (
                <TableRow key={vendor.id}>
                  <TableCell>{vendor.name}</TableCell>
                  <TableCell>
                    <Box sx={{ 
                      display: 'inline-block',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: vendor.status === 'Active' ? '#e8f5e8' : '#ffe8e8',
                      color: vendor.status === 'Active' ? '#2e7d32' : '#d32f2f',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {vendor.status}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {hasPermission('edit-vendor') && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setEditingVendor({ ...vendor });
                          setShowEditVendorDialog(true);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Vendor Dialog */}
      <Dialog open={showAddVendorDialog} onClose={() => setShowAddVendorDialog(false)}>
        <DialogTitle>Add New Vendor</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Vendor Name"
            fullWidth
            value={newVendor.name}
            onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddVendorDialog(false)} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleAddVendor} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : 'Add Vendor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Vendor Dialog */}
      <Dialog open={showEditVendorDialog} onClose={() => setShowEditVendorDialog(false)}>
        <DialogTitle>Edit Vendor</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Vendor Name"
            fullWidth
            value={editingVendor?.name || ''}
            disabled={true}
            helperText="Vendor name cannot be changed"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              value={editingVendor?.status || 'Active'}
              onChange={(e) => setEditingVendor({ ...editingVendor!, status: e.target.value })}
              label="Status"
              disabled={isSubmitting}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Blocked">Blocked</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditVendorDialog(false)} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleEditVendor} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : 'Update Vendor'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Vendors;

